import { useState } from "react";
import {
  useGetHourlyPositionsQuery,
  useGetMyHourlyPositionsQuery,
  useCloseHourlyPositionMutation,
  useDcaHourlyPositionMutation,
} from "@/store/api";
import { useAppSelector } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PnLPill } from "@/components/domain/PnLPill";
import {
  UserFilter, type UserFilterValue, matchesUserFilter,
} from "@/components/domain/UserFilter";
import { fmtUsd, fmtTime, fmtRelative } from "@/lib/format";
import { toast } from "sonner";

interface DcaFill {
  ts: string; fill_price: number; qty_added: number;
  size_usd_added: number; fee_added: number;
}

function parseFills(raw: string | DcaFill[] | null | undefined): DcaFill[] {
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return []; }
  }
  if (Array.isArray(raw)) return raw;
  return [];
}

function DcaSummary({
  fills, fillsUsed, totalAdded, postAvgPrice, origSizeUsd, totalQty,
}: {
  fills: string | DcaFill[] | null | undefined;
  fillsUsed: number; totalAdded: number; postAvgPrice: number;
  origSizeUsd: number; totalQty: number;
}) {
  const parsed = parseFills(fills);
  // Derive the original entry price so users can see why the post-DCA
  // average landed where it did. orig_qty = total qty - sum(dca qty);
  // orig_entry = orig_size_usd / orig_qty. Falls back gracefully when
  // legacy rows don't have per-fill detail.
  const dcaQty = parsed.reduce((acc, f) => acc + (f.qty_added || 0), 0);
  const origQty = totalQty - dcaQty;
  const origEntry =
    origQty > 0 && origSizeUsd > 0 ? origSizeUsd / origQty : null;
  return (
    <div className="text-xs text-accent mt-1 space-y-0.5">
      <div className="font-semibold">
        +{fillsUsed} DCA · +{fmtUsd(totalAdded)}{" "}
        <span className="text-muted font-normal">
          {origEntry != null ? `· orig ${origEntry.toFixed(6)} ` : ""}
          → avg {postAvgPrice.toFixed(6)}
        </span>
      </div>
      {parsed.length === 0 ? (
        <div className="text-muted">
          (per-fill detail not recorded for fills before the upgrade)
        </div>
      ) : (
        parsed.map((f, i) => (
          <div key={i} className="text-muted">
            #{i + 1} {fmtUsd(f.size_usd_added)} @ {f.fill_price.toFixed(6)}{" "}
            <span className="text-muted/70">
              · qty +{f.qty_added.toFixed(4)}{" "}
              {f.ts ? `· ${fmtRelative(f.ts)}` : ""}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/** Hourly bot — open positions card. Reused on Overview and Cycles.
 *  Admins see all open positions across the bot; regular users see only
 *  their own (scoped via /api/me/hourly/positions). */
export function HourlyOpenPositions({ pollMs = 30000 }: { pollMs?: number }) {
  const isAdmin = useAppSelector((s) => s.auth.user?.role === "admin");
  const adminQ = useGetHourlyPositionsQuery(
    { status: "open" }, { pollingInterval: pollMs, skip: !isAdmin },
  );
  const myQ = useGetMyHourlyPositionsQuery(
    { status: "open" }, { pollingInterval: pollMs, skip: isAdmin },
  );
  const open = isAdmin ? adminQ : myQ;
  const [closeHourly] = useCloseHourlyPositionMutation();
  const [dcaHourly, { isLoading: dcaPosting }] = useDcaHourlyPositionMutation();
  const [userFilter, setUserFilter] = useState<UserFilterValue>(null);
  const allRows = open.data ?? [];
  const rows = isAdmin
    ? allRows.filter((r: any) => matchesUserFilter(r, userFilter))
    : allRows;
  if (allRows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle>
          Hourly bot — open positions
          <span className="text-xs text-muted ml-2 font-normal">
            AI-set target/stop · monitor every 60s
            {isAdmin && userFilter !== null && (
              <> · showing {rows.length} of {allRows.length}</>
            )}
          </span>
        </CardTitle>
        {isAdmin && (
          <UserFilter value={userFilter} onChange={setUserFilter} includeUnassigned />
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <THead>
            <TR>
              <TH>Symbol</TH>
              {isAdmin && <TH>Owner</TH>}
              <TH className="text-right">Entry</TH>
              <TH className="text-right">Last</TH>
              <TH className="text-right">P&amp;L</TH>
              <TH className="text-right">Target</TH>
              <TH className="text-right">Stop</TH>
              <TH className="text-right">Size</TH>
              <TH>Reasoning</TH>
              <TH>Opened</TH>
              <TH></TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((p: any) => {
              const last = p.current_price != null ? +p.current_price : null;
              const pctChange = p.pct_change != null ? +p.pct_change : null;
              const unrealized = p.unrealized_net_usd != null ? +p.unrealized_net_usd : null;
              const pctToTarget = p.pct_to_target != null ? +p.pct_to_target : null;
              const pctToStop = p.pct_to_stop != null ? +p.pct_to_stop : null;
              const trailArmed = !!p.trail_active;
              const trailStop = p.trail_stop != null ? +p.trail_stop : null;
              const prevTrailStop = p.prev_trail_stop != null ? +p.prev_trail_stop : null;
              const gainLocked = p.gain_locked_pct != null ? +p.gain_locked_pct : null;
              const prevGainLocked = p.prev_gain_locked_pct != null ? +p.prev_gain_locked_pct : null;
              const pctToEffStop = p.pct_to_effective_stop != null ? +p.pct_to_effective_stop : null;
              const highWater = p.trail_high_water != null ? +p.trail_high_water : null;
              const trailDist = p.trail_distance_pct != null ? +p.trail_distance_pct : null;
              const trailActPx = p.activation_price != null ? +p.activation_price : null;
              const lastMovedAt = p.trail_last_moved_at as string | null;
              return (
                <TR key={p.id}>
                  <TD className="font-medium">{p.symbol}</TD>
                  {isAdmin && (
                    <TD className="text-xs">
                      {p.user_id != null ? (
                        <>
                          <span className="text-muted">#{p.user_id}</span>{" "}
                          <span>{p.user_email ?? ""}</span>
                        </>
                      ) : <span className="text-muted/60">—</span>}
                    </TD>
                  )}
                  <TD className="text-right tabular">{(+p.entry_price).toFixed(6)}</TD>
                  <TD className="text-right tabular">
                    {last != null ? last.toFixed(6) : "—"}
                    {pctChange != null && (
                      <div className={"text-xs " + (pctChange >= 0 ? "text-good" : "text-bad")}>
                        {pctChange >= 0 ? "+" : ""}{(pctChange * 100).toFixed(2)}%
                      </div>
                    )}
                  </TD>
                  <TD className="text-right">
                    <PnLPill value={unrealized} />
                    {trailArmed ? (
                      <div className="text-xs text-good" title={
                        `High water: ${highWater?.toFixed(6)}\n` +
                        `Trail distance: ${trailDist != null ? (trailDist * 100).toFixed(2) + "%" : "—"}\n` +
                        (lastMovedAt ? `Trail moved: ${fmtRelative(lastMovedAt)}` : "")
                      }>
                        trail armed
                        {gainLocked != null && (
                          <span className="ml-1">
                            · locked {gainLocked >= 0 ? "+" : ""}{(gainLocked * 100).toFixed(2)}%
                          </span>
                        )}
                      </div>
                    ) : trailActPx != null && last != null ? (
                      <div className="text-xs text-muted">
                        trail at {trailActPx.toFixed(6)} (
                        {(((trailActPx - last) / last) * 100).toFixed(2)}% away)
                      </div>
                    ) : null}
                  </TD>
                  <TD className="text-right tabular">
                    {(+p.target_price).toFixed(6)}
                    <div className="text-xs text-muted">
                      +{((+p.target_pct) * 100).toFixed(2)}%
                      {pctToTarget != null && (
                        <span className="ml-1">· {(pctToTarget * 100).toFixed(2)}% away</span>
                      )}
                    </div>
                  </TD>
                  <TD className="text-right tabular">
                    {trailArmed && trailStop != null ? (
                      <>
                        <div className="text-good">{trailStop.toFixed(6)}</div>
                        <div className="text-xs text-muted">
                          {prevTrailStop != null ? (
                            <>was {prevTrailStop.toFixed(6)} → now {trailStop.toFixed(6)}</>
                          ) : (
                            <>trail · {pctToEffStop != null ? `${(pctToEffStop * 100).toFixed(2)}% away` : ""}</>
                          )}
                        </div>
                        {prevGainLocked != null && gainLocked != null && (
                          <div className="text-xs text-muted">
                            locked {(prevGainLocked * 100).toFixed(2)}% → {(gainLocked * 100).toFixed(2)}%
                          </div>
                        )}
                        <div className="text-xs text-muted line-through">
                          orig {(+p.stop_price).toFixed(6)}
                        </div>
                      </>
                    ) : (
                      <>
                        {(+p.stop_price).toFixed(6)}
                        <div className="text-xs text-muted">
                          −{((+p.stop_pct) * 100).toFixed(2)}%
                          {pctToStop != null && (
                            <span className="ml-1">· {(pctToStop * 100).toFixed(2)}% away</span>
                          )}
                        </div>
                      </>
                    )}
                  </TD>
                  <TD className="text-right tabular">
                    {fmtUsd(+p.size_usd + (+p.dca_total_added_usd || 0))}
                    {(+p.dca_total_added_usd > 0) && (
                      <div className="text-xs text-muted">
                        orig {fmtUsd(+p.size_usd)} + DCA{" "}
                        {fmtUsd(+p.dca_total_added_usd)}
                      </div>
                    )}
                    {(+p.dca_fills_used > 0) && (
                      <DcaSummary fills={p.dca_fills_json}
                                  fillsUsed={+p.dca_fills_used}
                                  totalAdded={+p.dca_total_added_usd}
                                  postAvgPrice={+p.entry_price}
                                  origSizeUsd={+p.size_usd}
                                  totalQty={+p.qty} />
                    )}
                  </TD>
                  <TD className="text-xs text-muted max-w-md">{p.planner_reasoning || "(default)"}</TD>
                  <TD className="text-xs text-muted">{fmtTime(p.entry_time)}</TD>
                  <TD>
                    {isAdmin && (
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!confirm(`Close hourly position ${p.id} (${p.symbol}) at market?`)) return;
                            try {
                              await closeHourly(p.id).unwrap();
                              toast.success("Closed");
                            } catch {
                              toast.error("Close failed");
                            }
                          }}
                        >
                          Close now
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={dcaPosting}
                          onClick={async () => {
                            if (!confirm(`DCA into ${p.symbol} (pid ${p.id}) — buy more at current market price?`)) return;
                            try {
                              const r: any = await dcaHourly(p.id).unwrap();
                              if (r?.status === "filled") {
                                toast.success(
                                  `DCA #${r.fill_n} filled @ ${(+r.fill_price).toFixed(6)} — new avg ${(+r.new_entry_price).toFixed(6)}`,
                                );
                              } else {
                                toast.warning(
                                  `DCA skipped: ${r?.reason || r?.status || "unknown"}`,
                                );
                              }
                            } catch (e: any) {
                              toast.error(e?.data?.detail ?? "DCA failed");
                            }
                          }}
                        >
                          DCA now
                        </Button>
                      </div>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </CardContent>
    </Card>
  );
}
